import { useEffect, useMemo, useState } from 'react'
import { useLocation } from 'react-router-dom'
import type { AgenteMonedaView, WrapperResponse } from '../../../model/kartera-api-model'
import { useHistory } from '../../../router/history'
import APIInvoke from '../../../utils/APIInvoke'
import {
    CURRENCIES_PATH,
    currencyPath,
    errorMessage,
    formatAmount,
    formatExample,
    NEW_CURRENCY_PATH,
} from '../common/currency-common'
import type { CurrenciesLocationState, FormMessage } from '../common/currency-common'

const LOAD_ERROR = 'No se pudo cargar el listado de monedas.'
const DELETE_ERROR = 'No se pudo eliminar la moneda.'

export interface CurrencyRow {
    id: number
    code: string
    name: string
    country: string
    rate: string
    format: string
}

const toRow = (moneda: AgenteMonedaView): CurrencyRow => ({
    id: moneda.id,
    code: moneda.abreviacion || '—',
    name: moneda.moneda || '',
    country: moneda.pais || '',
    rate: `$${formatAmount(Number(moneda.tipoCambio) || 0)}`,
    format: formatExample(moneda.formato),
})

const normalize = (value: string) => value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase()

export const useCurrencyListModel = () => {
    const history = useHistory()
    const location = useLocation()
    const locationState = location.state as CurrenciesLocationState | null

    const [search, setSearch] = useState('')
    const [reloadToken, setReloadToken] = useState(0)
    const [loading, setLoading] = useState(true)
    const [initialLoadDone, setInitialLoadDone] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [message, setMessage] = useState<FormMessage | null>(locationState?.flash ? { type: 'success', message: locationState.flash } : null)
    const [items, setItems] = useState<AgenteMonedaView[]>([])
    const [deleteId, setDeleteId] = useState<number | null>(null)
    const [deleting, setDeleting] = useState(false)

    useEffect(() => {
        if (locationState?.flash) history.replace(CURRENCIES_PATH)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    useEffect(() => {
        let active = true
        const load = async () => {
            try {
                setLoading(true)
                setError(null)
                const response: WrapperResponse<AgenteMonedaView[]> | null = await (await APIInvoke.invokeGET('/monedas')).json()
                if (!active) return
                if (!response?.ok) throw new Error(response?.message || LOAD_ERROR)
                setItems((response.body || []).filter((item) => item.agenteId != null))
                setInitialLoadDone(true)
            } catch (loadError) {
                if (active) setError(errorMessage(loadError, LOAD_ERROR))
            } finally {
                if (active) setLoading(false)
            }
        }
        load()
        return () => { active = false }
    }, [reloadToken])

    const rows = useMemo(() => {
        const query = normalize(search.trim())
        const all = items.map(toRow).sort((left, right) => left.code.localeCompare(right.code))
        if (!query) return all
        return all.filter((row) => normalize(`${row.code} ${row.name} ${row.country}`).includes(query))
    }, [items, search])

    const confirmDelete = async () => {
        if (deleteId == null || deleting) return
        try {
            setDeleting(true)
            setMessage(null)
            const response: WrapperResponse<null> = await (await APIInvoke.invokeDELETE(`/monedas/${deleteId}`)).json()
            if (response.ok) {
                setItems((current) => current.filter((item) => item.id !== deleteId))
                setMessage({ type: 'success', message: 'Moneda eliminada correctamente' })
            } else {
                setMessage({ type: 'error', message: response.message || DELETE_ERROR })
            }
        } catch (deleteError) {
            console.error(deleteError)
            setMessage({ type: 'error', message: DELETE_ERROR })
        } finally {
            setDeleting(false)
            setDeleteId(null)
        }
    }

    const currencyToDelete = rows.find((row) => row.id === deleteId)

    return {
        search,
        loading,
        initialLoadDone,
        error,
        message,
        rows,
        totalItems: items.length,
        newLocation: { pathname: NEW_CURRENCY_PATH, state: { backUrl: CURRENCIES_PATH } },
        deleteOpen: deleteId != null,
        deleteLabel: currencyToDelete ? `${currencyToDelete.code} · ${currencyToDelete.name}` : 'esta moneda',
        deleting,
        changeSearch: setSearch,
        clearSearch: () => setSearch(''),
        reload: () => setReloadToken((token) => token + 1),
        editCurrency: (id: number) => history.push({ pathname: currencyPath(id), state: { backUrl: CURRENCIES_PATH } }),
        askDelete: (id: number) => setDeleteId(id),
        cancelDelete: () => setDeleteId(null),
        confirmDelete,
        dismissMessage: () => setMessage(null),
    }
}
